import { useSubscriptionState } from './useSubscriptionState'
import { useSubscriptionFlow } from './useSubscriptionFlow'
import { destinationCountry, nationality } from './useSubscription'
import {
  buildContractDraft,
  createContractDraft,
  isEmailTakenError,
  type ContractDraft,
  type ContractDraftData,
  type SubscriberInfo,
} from '@/services/subscription'
import { ApiError } from '@/services/http'
import { subscriptionFlowFor } from '@/subscriptionFlows'
import { useAuth } from './useAuth'

/**
 * Resolve a subscription contract draft from the current journey.
 *
 * The draft payload is derived from URL/route-backed funnel state
 * (`useSubscriptionState` / `useSubscriptionFlow`), so `buildDraftData` must be
 * called from a component/composable mounted on a journey route — off-route the
 * product and answers no longer resolve.
 *
 * Shared by `useSubscriptionFinalize` (Payer / Partager) and the resolver's
 * consumers so the draft-building and error-mapping logic lives in one place.
 */
export function useContractDraft() {
  const { answers, product } = useSubscriptionState()
  const { contractInputs } = useSubscriptionFlow()
  const { userId } = useAuth()

  /**
   * Build the contract-draft payload for the active journey, or `null` when
   * there is no subscription flow or the bundle/period isn't resolved yet
   * (nothing to subscribe to). Synchronous: no network — the caller persists it.
   */
  function buildDraftData(info: SubscriberInfo): ContractDraftData | null {
    const flow = subscriptionFlowFor(product.value)
    const contracts = contractInputs.value
    if (!flow || contracts.length === 0) return null

    return buildContractDraft({
      info,
      contracts,
      subscriberProfile: flow.subscriberProfile(answers.value),
      tacitRenewal: flow.tacitRenewal,
      metadata: flow.contractMetadata(answers.value, {
        destinationCountry: destinationCountry.value,
        nationality: nationality.value,
      }),
      // Logged-in subscriber: send only user_id + user_role. Null → guest draft
      // with the full info collected in the journey.
      userId: userId.value,
    })
  }

  return { buildDraftData }
}

/**
 * Map a failure of `createContractDraft` to an i18n key. An account already
 * existing for the subscriber's email (422 + taken) points the user to login
 * rather than showing a generic validation error.
 */
export function mapDraftError(e: unknown): string {
  if (e instanceof ApiError && e.status === 422) {
    return isEmailTakenError(e.body)
      ? 'subscription.info.payment.emailTaken'
      : 'subscription.info.payment.invalid'
  }
  return 'subscription.info.payment.error'
}

/* ------------------------------------------------------------------
 * Create-once store for the payment screen's contract draft.
 *
 * Every payment action on the recap (Payer, Partager) must target the SAME
 * `contract_draft`: the backend keys the shareable Payment Link and every
 * Checkout Session to a single draft, and neutralizes a draft's pending
 * link/sessions when it is paid. Creating a fresh draft per attempt yields a new
 * link each time and leaves orphaned links/sessions on old drafts → double-charge
 * risk. So we create the draft once per configuration and reuse its id.
 *
 * Reuse is keyed by a fingerprint of the draft `data`: an unchanged configuration
 * reuses the draft (no network), while editing the info/answers/RIB yields a new
 * fingerprint and therefore a new draft (correct — a different config must never
 * reuse a stale draft). The store is backed by `sessionStorage` so the draft id
 * survives the full page reload that the Payer button triggers by handing the
 * browser to Stripe (`window.location.assign`) — on a Stripe cancel the funnel is
 * restored to the recap and the first click re-adopts the persisted draft instead
 * of creating a new one. We persist only a HASH of the fingerprint (never the raw
 * payload, which holds name/email/SSN/IBAN), plus the opaque draft id/status.
 * The stored key lives under the `smerra:subscription:` prefix that ThankYouView
 * sweeps on payment success, so a completed payment forgets the draft for free.
 * ------------------------------------------------------------------*/

/**
 * Fingerprint of a draft payload. `JSON.stringify` is stable here because
 * `buildContractDraft` always emits the same key order (fixed object literals +
 * conditional spreads), so two identical configurations stringify identically.
 * A future reshuffle of that builder would need a matching update here.
 */
function draftFingerprint(data: ContractDraftData): string {
  return JSON.stringify(data)
}

/**
 * cyrb53 — a small, fast, non-cryptographic 53-bit string hash (public domain,
 * by bryc). Used to persist a collision-resistant fingerprint WITHOUT writing the
 * raw payload (and its personal data) to `sessionStorage`.
 */
function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed
  let h2 = 0x41c6ce57 ^ seed
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return 4294967296 * (2097151 & h2) + (h1 >>> 0)
}

/** Under the `smerra:subscription:` prefix so ThankYouView's success sweep clears it. */
const DRAFT_SS_KEY = 'smerra:subscription:draft'

/** Persisted breadcrumb: config hash + the opaque draft (id/status, no PII). */
type StoredDraft = { h: number; draft: ContractDraft }

function readStoredDraft(): StoredDraft | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(DRAFT_SS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed.h === 'number' &&
      parsed.draft &&
      typeof parsed.draft.id === 'string'
    ) {
      return parsed as StoredDraft
    }
  } catch {
    /* storage disabled / corrupt JSON */
  }
  return null
}

function writeStoredDraft(h: number, draft: ContractDraft): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(DRAFT_SS_KEY, JSON.stringify({ h, draft }))
  } catch {
    /* private mode / quota */
  }
}

// In-memory layer: exact fingerprint match for certainty/speed within a live page
// session; `sessionStorage` (hashed) is only the cross-reload breadcrumb.
let cachedDraft: { fingerprint: string; draft: ContractDraft } | null = null
let inFlightDraft: { fingerprint: string; promise: Promise<ContractDraft> } | null = null

/**
 * Resolve the contract draft for a configuration, creating it at most once and
 * reusing it for identical configurations across clicks, off-route navigation and
 * the Stripe reload. Concurrent calls for the same fingerprint share one in-flight
 * request. A failed creation is never cached or persisted, so a retry re-issues
 * it. No `AbortSignal`: the promise is shared across callers.
 */
export async function resolveContractDraft(data: ContractDraftData): Promise<ContractDraft> {
  const fingerprint = draftFingerprint(data)
  const h = cyrb53(fingerprint)

  // 1) Exact in-memory match (same live session).
  if (cachedDraft && cachedDraft.fingerprint === fingerprint) return cachedDraft.draft
  // 2) A creation for this config is already in flight.
  if (inFlightDraft && inFlightDraft.fingerprint === fingerprint) return inFlightDraft.promise
  // 3) Rehydrate from sessionStorage (survives the Stripe reload). Hash match only.
  const stored = readStoredDraft()
  if (stored && stored.h === h) {
    cachedDraft = { fingerprint, draft: stored.draft }
    return stored.draft
  }

  // 4) Nothing reusable — create once, then cache and persist.
  const promise = createContractDraft(data)
    .then((draft) => {
      cachedDraft = { fingerprint, draft }
      writeStoredDraft(h, draft)
      return draft
    })
    .finally(() => {
      if (inFlightDraft && inFlightDraft.fingerprint === fingerprint) inFlightDraft = null
    })
  inFlightDraft = { fingerprint, promise }
  return promise
}
